import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { StatCard, PageHeader, LoadingPage, EmptyState } from '../../components/common/UI';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';

const NAV = [
  { id: 'overview', label: 'Overview',   icon: '📊' },
  { id: 'coupons',  label: 'My Deals',   icon: '🏷️' },
  { id: 'submit',   label: 'Submit Deal',icon: '+'  },
  { id: 'billing',  label: 'Billing',    icon: '💳' },
  { id: 'settings', label: 'Settings',   icon: '⚙️' },
];

export default function MerchantPortal() {
  const [tab, setTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [coupons, setCoupons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/merchant/analytics'),
      api.get('/merchant/coupons'),
    ]).then(([a, c]) => {
      setAnalytics(a.data);
      setCoupons(c.data.data || []);
      setAccessError(null);
    }).catch(err => {
      // 403 from merchant.only middleware: user is merchant_admin but not yet
      // linked to a merchant by an admin. 401 means session is dead (rare —
      // api.js redirects on its own).
      if (err.response?.status === 403) {
        setAccessError(err.response.data?.error || 'Your account is not yet linked to a merchant. Contact support.');
      } else if (err.response?.status === 401) {
        setAccessError('Please sign in again.');
      } else {
        setAccessError('Could not load merchant data. Please try again.');
      }
    }).finally(() => setLoading(false));
  }, []);

  const refreshCoupons = async () => {
    try {
      const { data } = await api.get('/merchant/coupons');
      setCoupons(data.data || []);
    } catch {}
  };

  return (
    <div className="portal">
      <PortalSidebar active={tab} onChange={setTab} />
      <main className="portal-main">
        {loading ? <LoadingPage /> : accessError ? (
          <div style={{ padding: 32 }}>
            <PageHeader title="Merchant Portal" subtitle="Awaiting setup" />
            <div style={{ background: '#fff', border: '1px solid #E4E7F0', borderRadius: 16, padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Almost ready</h3>
              <p style={{ color: '#4A5568', fontSize: 14, lineHeight: 1.6, maxWidth: 480, margin: '0 auto' }}>{accessError}</p>
              <p style={{ color: '#8B94A8', fontSize: 13, marginTop: 16 }}>
                Once an admin approves your account and links it to your merchant profile, you'll see your dashboard here.
              </p>
              <Link to="/" className="btn btn-secondary" style={{ marginTop: 24, display: 'inline-block' }}>← Back to site</Link>
            </div>
          </div>
        ) : (
          <>
            {tab === 'overview' && <OverviewTab analytics={analytics} />}
            {tab === 'coupons'  && <CouponsTab coupons={coupons} onSubmit={() => setTab('submit')} />}
            {tab === 'submit'   && <SubmitDealForm onSuccess={() => { setTab('coupons'); refreshCoupons(); }} />}
            {tab === 'billing'  && <BillingTab />}
            {tab === 'settings' && <SettingsTab />}
          </>
        )}
      </main>
    </div>
  );
}

/* ============================= Sidebar ============================= */

function PortalSidebar({ active, onChange }) {
  const { user } = useAuth();
  return (
    <aside className="portal-aside">
      <div className="portal-aside-top">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div className="portal-aside-brand">
            <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--brand-gradient)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800 }}>P</span>
            <span>PerkVora</span>
          </div>
          <p className="portal-aside-label">Merchant Portal</p>
        </Link>
      </div>

      <nav className="portal-aside-nav">
        {NAV.map(item => (
          <button key={item.id} onClick={() => onChange(item.id)}
            className={`portal-nav-item ${active === item.id ? 'active' : ''}`}
            title={item.label}
          >
            <span className="portal-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="portal-aside-footer">
        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {user.name?.[0]?.toUpperCase() || 'M'}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || 'Merchant'}</p>
              <p style={{ fontSize: 11, color: '#7A7F9E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</p>
            </div>
          </div>
        )}
        <Link to="/" className="portal-nav-item">
          <span className="portal-nav-icon">←</span>
          <span>Back to site</span>
        </Link>
      </div>
    </aside>
  );
}

/* ============================= Overview ============================= */

function OverviewTab({ analytics }) {
  const stats = analytics || {};
  return (
    <>
      <PageHeader
        title="Performance Overview"
        subtitle="All-time stats for your brand on PerkVora"
        action={<span className="chip" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid #C3E6CB' }}>● Live</span>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard gradient label="Total Redemptions"  value={(stats.total_redemptions ?? 0).toLocaleString()} icon="🎟️" />
        <StatCard        label="Commission Earned"   value={`$${Number(stats.total_commission ?? 0).toFixed(2)}`} icon="💰" iconBg="linear-gradient(135deg,#0F9D6E,#059669)" />
        <StatCard        label="Impressions"         value={(stats.total_impressions ?? 0).toLocaleString()} icon="👁️" iconBg="linear-gradient(135deg,#F59E0B,#D97706)" />
        <StatCard        label="Clicks"              value={(stats.total_clicks ?? 0).toLocaleString()} icon="🖱️" iconBg="linear-gradient(135deg,#6B2FD9,#4C1D95)" />
      </div>

      {stats.top_coupons?.length > 0 ? (
        <div className="ds-panel">
          <div className="ds-panel-head">
            <span className="ds-panel-title">Top Performing Deals</span>
            <span style={{ fontSize: 12, color: '#8B94A8' }}>Ranked by redemptions</span>
          </div>
          <div className="ds-panel-body-flush">
            <table className="ds-table">
              <thead><tr><th>Rank</th><th>Deal</th><th style={{ textAlign: 'right' }}>Redemptions</th><th style={{ textAlign: 'right' }}>Conversion</th></tr></thead>
              <tbody>
                {stats.top_coupons.map((c, i) => (
                  <tr key={c.id}>
                    <td><span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: 15 }} className={i === 0 ? 'gradient-text' : ''}>#{i + 1}</span></td>
                    <td style={{ fontWeight: 600 }}>{c.title}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{c.redemptions_count?.toLocaleString() || 0}</td>
                    <td style={{ textAlign: 'right', color: '#8B94A8', fontSize: 13 }}>{c.conversion_rate ? `${c.conversion_rate.toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState icon="📈" title="No performance data yet" subtitle="Once your first deal goes live, you'll see real-time stats here." />
      )}
    </>
  );
}

/* ============================= My Deals ============================= */

function CouponsTab({ coupons, onSubmit }) {
  const [search, setSearch] = useState('');
  const filtered = coupons.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <PageHeader
        title="My Deals"
        subtitle={`${coupons.length} deals total`}
        action={<button className="btn btn-primary" onClick={onSubmit}>+ Submit New Deal</button>}
      />

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#8B94A8', fontSize: 14 }}>🔍</span>
          <input className="form-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your deals…" style={{ paddingLeft: 38 }} />
        </div>
      </div>

      <div className="ds-panel">
        <div className="ds-panel-body-flush" style={{ overflowX: 'auto' }}>
          <table className="ds-table">
            <thead>
              <tr><th>Deal</th><th>Discount</th><th style={{ textAlign: 'right' }}>Impressions</th><th style={{ textAlign: 'right' }}>Clicks</th><th style={{ textAlign: 'right' }}>Redemptions</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6}>
                  <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                    <p style={{ fontSize: 36, marginBottom: 10 }}>📭</p>
                    {/* No button here on purpose. The sidebar "Submit Deal"
                        item and the "+ Submit New Deal" action in the header
                        are both already on screen; a third control for the
                        same flow, one of them inches away, read as clutter. */}
                    <p style={{ fontSize: 14, color: '#8B94A8' }}>{coupons.length === 0 ? 'No deals yet — use “Submit New Deal” above to go live.' : 'No results.'}</p>
                  </div>
                </td></tr>
              ) : filtered.map(c => (
                <tr key={c.id}>
                  <td><p style={{ fontWeight: 600, color: '#0F1225' }}>{c.title}</p>{c.expires_at && <p style={{ fontSize: 11, color: '#8B94A8', marginTop: 2 }}>Ends {new Date(c.expires_at).toLocaleDateString()}</p>}</td>
                  <td><span className="badge badge-gradient">{c.discount_value}{c.discount_type === 'percent' ? '%' : c.discount_type === 'fixed' ? '$' : ''} OFF</span></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(c.impressions_count ?? 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(c.clicks_count ?? 0).toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{(c.redemptions_count ?? 0).toLocaleString()}</td>
                  <td><span className={`badge ${c.is_active ? 'badge-green' : 'badge-yellow'}`}>{c.is_active ? '● Live' : '● Pending'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ============================= Submit ============================= */

function SubmitDealForm({ onSuccess }) {
  const [form, setForm] = useState({
    title: '', description: '', discount_type: 'percent', discount_value: '',
    code_type: 'shared', shared_code: '', max_uses: '', expires_at: '', terms: '',
  });
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');
  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post('/merchant/coupons', form);
      setSuccess(true);
      setTimeout(onSuccess, 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit. Please try again.');
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: 20, border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', maxWidth: 560, margin: '40px auto' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 36, color: '#fff' }}>✓</div>
        <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>Deal submitted!</h2>
        <p style={{ fontSize: 15, color: '#4A5568' }}>Your deal is pending review and will go live shortly. Redirecting…</p>
      </div>
    );
  }

  return (
    <>
      {/* Slim header on this tab specifically — the standard PageHeader
          renders a heading + subtitle inside .portal-topbar which carries
          a 28px bottom margin. Other tabs need that breathing room for
          their action button row; this form has no action button at the
          top, so the margin reads as "wide empty band above the form"
          (Ananya 06-26 / 06-30 #2). Use a tighter inline header instead. */}
      <div style={{ marginBottom: 14 }}>
        <h1 className="portal-heading">Submit a New Deal</h1>
        <p className="portal-sub">Deals go through a quick review and typically go live within 24 hours.</p>
      </div>

      <div className="ds-panel" style={{ maxWidth: 880 }}>
        <div className="ds-panel-body" style={{ padding: '14px 20px 20px' }}>
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginTop: 0 }}>
                <label className="form-label">Deal Title *</label>
                <input className="form-input" required placeholder="e.g. 25% off your first order" {...f('title')} />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={3} placeholder="Describe the offer and what customers get" {...f('description')} style={{ minHeight: 80 }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Discount Type *</label>
                  <select className="form-input form-select" {...f('discount_type')}>
                    <option value="percent">Percent Off</option>
                    <option value="fixed">Fixed $ Off</option>
                    <option value="freebie">Free Item</option>
                    <option value="bogo">Buy One Get One</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Value {form.discount_type === 'percent' ? '(%)' : '($)'}  *</label>
                  <input className="form-input" type="number" min="0" required {...f('discount_value')} placeholder="20" />
                </div>

                <div className="form-group">
                  <label className="form-label">Code Type *</label>
                  <select className="form-input form-select" {...f('code_type')}>
                    <option value="shared">Shared Promo Code</option>
                    <option value="unique">Unique Codes (one per user)</option>
                    <option value="no_code">No Code Needed</option>
                    <option value="affiliate_link">Affiliate Link</option>
                  </select>
                </div>

                {form.code_type === 'shared' && (
                  <div className="form-group">
                    <label className="form-label">Promo Code *</label>
                    <input className="form-input" placeholder="SAVE25" required {...f('shared_code')} style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', letterSpacing: 1 }} />
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Max Uses</label>
                  <input className="form-input" type="number" min="1" placeholder="Leave blank for unlimited" {...f('max_uses')} />
                </div>

                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <input className="form-input" type="date" {...f('expires_at')} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Terms & Conditions</label>
                <textarea className="form-input" rows={3} placeholder="Any restrictions, minimum order amounts, exclusions…" {...f('terms')} style={{ minHeight: 80 }} />
              </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button type="button" className="btn btn-secondary" onClick={() => onSuccess()}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                {saving ? 'Submitting…' : 'Submit for Review →'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

/* ============================= Billing ============================= */

function BillingTab() {
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/me/stripe-connect/onboard', {
        return_url:  `${origin}/merchant?stripe=ok`,
        refresh_url: `${origin}/merchant?stripe=restart`,
        country:     'US',
      });
      if (data?.url) {
        // Hand off to Stripe's hosted onboarding. Don't open a new tab —
        // the return_url brings them back to /merchant where the refresh
        // endpoint reconciles state.
        window.location.assign(data.url);
        return;
      }
      toast.error('Unexpected response from Stripe onboarding endpoint.');
    } catch (err) {
      const status = err.response?.status;
      const body   = err.response?.data || {};
      if (status === 503) {
        toast.error(body.error || 'Stripe is not configured on this environment yet.', body.detail);
      } else if (status === 401) {
        toast.error('Sign in again to connect a bank account.');
      } else {
        toast.error(body.error || 'Could not start Stripe onboarding.', body.detail);
      }
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
      <PageHeader title="Billing & Payouts" subtitle="Manage your commission settlements and payment methods" />
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
        <div className="ds-panel">
          <div className="ds-panel-head">
            <span className="ds-panel-title">Recent Transactions</span>
          </div>
          <div className="ds-panel-body">
            <EmptyState icon="💳" title="No transactions yet" subtitle="Once you start earning commission on redeemed deals, you'll see a detailed history here." />
          </div>
        </div>

        <div>
          <div className="ds-panel" style={{ marginBottom: 20 }}>
            <div className="ds-panel-head"><span className="ds-panel-title">Balance</span></div>
            <div className="ds-panel-body">
              <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 8 }}>Pending Payout</p>
              <p className="gradient-text" style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', lineHeight: 1 }}>$0.00</p>
              <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 8 }}>Next payout: first business day of next month</p>
              <button className="btn btn-outline btn-full" style={{ marginTop: 20 }} disabled>Request payout</button>
            </div>
          </div>
          <div className="ds-panel">
            <div className="ds-panel-head"><span className="ds-panel-title">Payment Method</span></div>
            <div className="ds-panel-body">
              <p style={{ fontSize: 13, color: '#8B94A8', marginBottom: 14 }}>No payment method on file.</p>
              <button className="btn btn-primary btn-full btn-sm" disabled={connecting} onClick={handleConnect}>
                {connecting ? 'Opening Stripe…' : 'Connect bank / Stripe'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================= Settings ============================= */

function SettingsTab() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/merchant/settings');
      setData(res.data);
      setForm({
        name:          res.data.name          ?? '',
        website:       res.data.website       ?? '',
        description:   res.data.description   ?? '',
        logo_url:      res.data.logo_url      ?? '',
        contact_email: res.data.contact_email ?? '',
        city:          res.data.city          ?? '',
        state:         res.data.state         ?? '',
        zip_code:      res.data.zip_code      ?? '',
        country:       res.data.country       ?? '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load settings.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const f = k => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = { ...form };
    // Send nulls for cleared optional fields so the backend trims the
    // value instead of treating the empty string as "no change".
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
    try {
      const { data: res } = await api.patch('/merchant/settings', payload);
      setData(d => ({ ...d, ...res.data }));
      toast.success('Settings saved');
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors) setError(Object.values(fieldErrors).flat()[0] || 'Save failed');
      else             setError(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingPage />;
  if (!data)   return <div className="ds-panel"><div className="ds-panel-body"><EmptyState icon="⚙️" title="Couldn't load settings" subtitle={error} /></div></div>;

  return (
    <>
      <PageHeader title="Settings" subtitle="Manage your brand profile, logo, and contact info." />

      <form onSubmit={save}>
        <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Brand identity */}
          <div className="ds-panel">
            <div className="ds-panel-head"><span className="ds-panel-title">Brand</span></div>
            <div className="ds-panel-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-group" style={{ marginTop: 0 }}>
                <label className="form-label">Business name *</label>
                <input className="form-input" required {...f('name')} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input className="form-input" type="url" placeholder="https://example.com" {...f('website')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact email</label>
                  <input className="form-input" type="email" placeholder="hello@example.com" {...f('contact_email')} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Logo URL</label>
                <input className="form-input" type="url" placeholder="https://…/logo.png" {...f('logo_url')} />
                {form.logo_url && (
                  <div style={{ marginTop: 10 }}>
                    <img src={form.logo_url} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', border: '1px solid var(--border)' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Brand description</label>
                <textarea className="form-input" rows={4} placeholder="A short blurb shoppers will see on your brand page." style={{ minHeight: 100 }} {...f('description')} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="ds-panel">
            <div className="ds-panel-head"><span className="ds-panel-title">Location</span></div>
            <div className="ds-panel-body">
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 90px', gap: 14 }}>
                <div className="form-group" style={{ marginTop: 0 }}>
                  <label className="form-label">City</label>
                  <input className="form-input" {...f('city')} />
                </div>
                <div className="form-group" style={{ marginTop: 0 }}>
                  <label className="form-label">State / Region</label>
                  <input className="form-input" {...f('state')} />
                </div>
                <div className="form-group" style={{ marginTop: 0 }}>
                  <label className="form-label">Postal code</label>
                  <input className="form-input" {...f('zip_code')} />
                </div>
                <div className="form-group" style={{ marginTop: 0 }}>
                  <label className="form-label">Country</label>
                  <input className="form-input" maxLength={2} placeholder="US" style={{ textTransform: 'uppercase' }} {...f('country')} />
                </div>
              </div>
            </div>
          </div>

          {/* Admin-managed (read-only) */}
          <div className="ds-panel">
            <div className="ds-panel-head"><span className="ds-panel-title">Managed by PerkVora admin</span></div>
            <div className="ds-panel-body">
              <p style={{ fontSize: 12.5, color: '#5A6378', marginBottom: 14 }}>
                These are negotiated as part of your partnership. To change them, email <a href="mailto:partners@perkvora.com">partners@perkvora.com</a>.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, fontSize: 13 }}>
                <ReadOnlyField label="Category"        value={data.read_only?.category_id ? `#${data.read_only.category_id}` : '—'} />
                <ReadOnlyField label="Affiliate network" value={(data.read_only?.affiliate_network && data.read_only.affiliate_network !== 'none') ? data.read_only.affiliate_network : 'Direct'} />
                <ReadOnlyField label="Commission %"    value={data.read_only?.commission_pct ? `${data.read_only.commission_pct}%` : '—'} />
                <ReadOnlyField label="Verified"        value={data.is_verified ? 'Yes' : 'Pending review'} accent={data.is_verified ? '#166534' : '#92400E'} />
                <ReadOnlyField label="Active"          value={data.is_active   ? 'Yes' : 'No'} accent={data.is_active ? '#166534' : '#991B1B'} />
              </div>
            </div>
          </div>

          {/* Save bar */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" disabled={saving} onClick={load}>Reset</button>
            <button type="submit" className="btn btn-primary"   disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      </form>
    </>
  );
}

function ReadOnlyField({ label, value, accent }) {
  return (
    <div>
      <p style={{ fontSize: 11, fontWeight: 700, color: '#8B94A8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 13, fontWeight: 600, color: accent || '#0F1225' }}>{value}</p>
    </div>
  );
}
